/**
 * ============================================================================
 * VOCALIRA CORE CONVERSATION ENGINE (VCCE) - Mobile Audio Engine
 * ============================================================================
 * 
 * SPRINT 2: Mobile-First Voice Engine
 * 
 * Este módulo gerencia:
 * - AudioContext lifecycle (unlock, resume, state monitoring)
 * - SampleRate detection e adaptação
 * - MediaStream acquisition com fallback
 * - Recovery automático de falhas de áudio
 * 
 * PRINCÍPIO: Um único motor adaptado por configuração, não forks de código.
 * 
 * @author Vocalira Team
 * @version 2.1.0 - Mobile-First Architecture
 */

import { getPlatformConfig } from './PlatformConfigurationManager.js';

// ============================================================================
// AUDIO CONTEXT MANAGER
// ============================================================================

/**
 * Gerenciador de AudioContext com suporte completo a mobile.
 * Lida com:
 * - Criação e unlock do AudioContext
 * - Monitoramento de estado
 * - Resume após background/foreground
 * - Adaptação de sampleRate
 */
class AudioContextManager {
  constructor(options = {}) {
    this.platformConfig = getPlatformConfig();
    this.config = this.platformConfig.getConfig();
    this.platform = this.platformConfig.getPlatform();
    
    // Estado
    this.audioContext = null;
    this.isUnlocked = false;
    this.actualSampleRate = null;
    
    // Callbacks
    this.onStateChange = options.onStateChange || (() => {});
    this.onError = options.onError || console.error;
    this.onLog = options.onLog || console.log;
    
    // Bind event handlers
    this._handleVisibilityChange = this._handleVisibilityChange.bind(this);
    this._handleUserGesture = this._handleUserGesture.bind(this);
    
    // Setup visibility listener
    document.addEventListener('visibilitychange', this._handleVisibilityChange);
    
    this._log('AUDIO_MANAGER_INITIALIZED', { platform: this.platform.platformId });
  }
  
  /**
   * Cria e configura o AudioContext.
   * @returns {Promise<AudioContext>}
   */
  async create() {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      
      if (!AudioContextClass) {
        throw new Error('AudioContext not supported');
      }
      
      // Criar com sampleRate preferido (pode ser ignorado pelo browser)
      this.audioContext = new AudioContextClass({
        sampleRate: this.config.preferredSampleRate,
      });
      
      // Verificar sampleRate real
      this.actualSampleRate = this.audioContext.sampleRate;
      
      this._log('AUDIOCONTEXT_CREATED', {
        requestedSampleRate: this.config.preferredSampleRate,
        actualSampleRate: this.actualSampleRate,
        state: this.audioContext.state,
        sampleRateHonored: this.actualSampleRate === this.config.preferredSampleRate,
      });
      
      // Notificar PlatformConfig sobre sampleRate real
      this.platformConfig.adjustForSampleRate(this.actualSampleRate);
      
      // Se requer unlock, não tentar resume ainda
      if (this.platformConfig.requiresAudioUnlock() && this.audioContext.state === 'suspended') {
        this._log('AUDIOCONTEXT_WAITING_UNLOCK', { state: this.audioContext.state });
        this.isUnlocked = false;
      } else {
        // Desktop: tentar resume direto
        await this._attemptResume();
      }
      
      // Monitor de estado
      this.audioContext.onstatechange = () => {
        this._log('AUDIOCONTEXT_STATE_CHANGED', { state: this.audioContext.state });
        this.onStateChange(this.audioContext.state);
        
        if (this.audioContext.state === 'running') {
          this.isUnlocked = true;
        }
      };
      
      return this.audioContext;
      
    } catch (error) {
      this._log('AUDIOCONTEXT_CREATE_ERROR', { error: error.message });
      this.onError(error);
      throw error;
    }
  }
  
  /**
   * Tenta fazer resume do AudioContext.
   * @returns {Promise<boolean>}
   */
  async _attemptResume() {
    if (!this.audioContext) return false;
    
    if (this.audioContext.state === 'running') {
      this.isUnlocked = true;
      return true;
    }
    
    if (this.audioContext.state === 'suspended') {
      try {
        await Promise.race([
          this.audioContext.resume(),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Resume timeout')), this.config.audioContextResumeTimeout)
          ),
        ]);
        
        this.isUnlocked = this.audioContext.state === 'running';
        this._log('AUDIOCONTEXT_RESUME_RESULT', { 
          state: this.audioContext.state,
          isUnlocked: this.isUnlocked,
        });
        
        return this.isUnlocked;
      } catch (error) {
        this._log('AUDIOCONTEXT_RESUME_FAILED', { error: error.message });
        return false;
      }
    }
    
    return false;
  }
  
  /**
   * Unlock explícito do AudioContext (deve ser chamado de um gesto do usuário).
   * @returns {Promise<boolean>}
   */
  async unlock() {
    this._log('AUDIOCONTEXT_UNLOCK_ATTEMPT');
    
    if (!this.audioContext) {
      await this.create();
    }
    
    const result = await this._attemptResume();
    
    if (result) {
      this._log('AUDIOCONTEXT_UNLOCKED', { state: this.audioContext.state });
    } else {
      this._log('AUDIOCONTEXT_UNLOCK_FAILED', { state: this.audioContext?.state });
    }
    
    return result;
  }
  
  /**
   * Handler para gestos do usuário (para unlock).
   */
  _handleUserGesture() {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      this.unlock();
    }
  }
  
  /**
   * Handler para mudanças de visibilidade (background/foreground).
   */
  _handleVisibilityChange() {
    if (document.visibilityState === 'visible' && this.audioContext) {
      this._log('VISIBILITY_CHANGED_TO_VISIBLE');
      
      // iOS e PWA podem suspender AudioContext quando em background
      if (this.audioContext.state !== 'running') {
        this._log('AUDIOCONTEXT_NEEDS_RESUME_AFTER_VISIBILITY');
        this._attemptResume();
      }
    }
  }
  
  /**
   * Verifica se o AudioContext está pronto para uso.
   */
  isReady() {
    return this.audioContext && 
           this.audioContext.state === 'running' && 
           this.isUnlocked;
  }
  
  /**
   * Retorna o sampleRate real.
   */
  getSampleRate() {
    return this.actualSampleRate || this.config.preferredSampleRate;
  }
  
  /**
   * Retorna o AudioContext.
   */
  getContext() {
    return this.audioContext;
  }
  
  /**
   * Fecha e limpa o AudioContext.
   */
  async close() {
    if (this.audioContext && this.audioContext.state !== 'closed') {
      try {
        await this.audioContext.close();
        this._log('AUDIOCONTEXT_CLOSED');
      } catch (error) {
        this._log('AUDIOCONTEXT_CLOSE_ERROR', { error: error.message });
      }
    }
    
    this.audioContext = null;
    this.isUnlocked = false;
    
    document.removeEventListener('visibilitychange', this._handleVisibilityChange);
  }
  
  /**
   * Log formatado.
   */
  _log(event, data = {}) {
    this.onLog(`[AudioContextManager] ${event}`, {
      ...data,
      timestamp: Date.now(),
      platform: this.platform.platformId,
    });
  }
}

// ============================================================================
// MEDIA STREAM MANAGER
// ============================================================================

/**
 * Gerenciador de MediaStream com fallback automático.
 */
class MediaStreamManager {
  constructor(options = {}) {
    this.platformConfig = getPlatformConfig();
    this.config = this.platformConfig.getConfig();
    this.platform = this.platformConfig.getPlatform();
    
    this.mediaStream = null;
    this.onLog = options.onLog || console.log;
    this.onError = options.onError || console.error;
  }
  
  /**
   * Adquire MediaStream do microfone com fallback.
   * @returns {Promise<MediaStream>}
   */
  async acquire() {
    // Tentar com constraints preferidas
    try {
      const constraints = this.platformConfig.getAudioConstraints();
      this._log('MEDIASTREAM_ACQUIRING', { constraints });
      
      this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      this._log('MEDIASTREAM_ACQUIRED', {
        tracks: this.mediaStream.getTracks().length,
        settings: this.mediaStream.getAudioTracks()[0]?.getSettings(),
      });
      
      return this.mediaStream;
      
    } catch (error) {
      this._log('MEDIASTREAM_ACQUIRE_FAILED', { 
        error: error.name,
        message: error.message,
      });
      
      // Se falhou com constraints específicas, tentar fallback
      if (error.name === 'OverconstrainedError' || error.name === 'NotFoundError') {
        return this._acquireFallback();
      }
      
      // Erros de permissão não têm fallback
      if (error.name === 'NotAllowedError') {
        this.onError(new Error('Permissão de microfone negada'));
        throw error;
      }
      
      throw error;
    }
  }
  
  /**
   * Fallback com constraints mínimas.
   */
  async _acquireFallback() {
    try {
      const constraints = this.platformConfig.getFallbackAudioConstraints();
      this._log('MEDIASTREAM_FALLBACK_ACQUIRING', { constraints });
      
      this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      this._log('MEDIASTREAM_FALLBACK_ACQUIRED', {
        tracks: this.mediaStream.getTracks().length,
      });
      
      return this.mediaStream;
      
    } catch (error) {
      this._log('MEDIASTREAM_FALLBACK_FAILED', { error: error.message });
      throw error;
    }
  }
  
  /**
   * Libera o MediaStream.
   */
  release() {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => {
        track.stop();
        this._log('MEDIASTREAM_TRACK_STOPPED', { kind: track.kind });
      });
      this.mediaStream = null;
    }
  }
  
  /**
   * Retorna o MediaStream atual.
   */
  getStream() {
    return this.mediaStream;
  }
  
  /**
   * Log formatado.
   */
  _log(event, data = {}) {
    this.onLog(`[MediaStreamManager] ${event}`, data);
  }
}

// ============================================================================
// STT RECOVERY MANAGER
// ============================================================================

/**
 * Gerenciador de recovery para SpeechRecognition.
 * Implementa restart inteligente com backoff exponencial.
 */
class STTRecoveryManager {
  constructor(options = {}) {
    this.platformConfig = getPlatformConfig();
    this.config = this.platformConfig.getConfig();
    
    this.restartAttempts = 0;
    this.lastRestartTime = null;
    this.isRecovering = false;
    
    this.onLog = options.onLog || console.log;
    this.onRecoveryExhausted = options.onRecoveryExhausted || (() => {});
    
    // Métricas
    this.metrics = {
      totalRestarts: 0,
      successfulRestarts: 0,
      failedRestarts: 0,
      exhaustedRecoveries: 0,
    };
  }
  
  /**
   * Tenta reiniciar o SpeechRecognition com backoff.
   * @param {SpeechRecognition} recognition - Instância do recognition
   * @param {string} reason - Motivo do restart
   * @returns {Promise<boolean>} - Se o restart foi bem sucedido
   */
  async attemptRestart(recognition, reason) {
    if (this.isRecovering) {
      this._log('STT_RECOVERY_ALREADY_IN_PROGRESS');
      return false;
    }
    
    if (this.restartAttempts >= this.config.sttMaxRestartAttempts) {
      this._log('STT_RECOVERY_EXHAUSTED', {
        attempts: this.restartAttempts,
        max: this.config.sttMaxRestartAttempts,
      });
      this.metrics.exhaustedRecoveries++;
      this.onRecoveryExhausted(reason);
      return false;
    }
    
    this.isRecovering = true;
    this.restartAttempts++;
    this.metrics.totalRestarts++;
    
    // Calcular delay com backoff exponencial
    const baseDelay = this.config.sttRestartDelay;
    const backoffDelay = this.config.sttRestartBackoffBase * Math.pow(2, this.restartAttempts - 1);
    const delay = Math.min(baseDelay + backoffDelay, 3000); // Max 3 segundos
    
    this._log('STT_RECOVERY_ATTEMPTING', {
      attempt: this.restartAttempts,
      maxAttempts: this.config.sttMaxRestartAttempts,
      delay,
      reason,
    });
    
    // Aguardar delay
    await new Promise(resolve => setTimeout(resolve, delay));
    
    // Tentar restart
    try {
      recognition.start();
      this.lastRestartTime = Date.now();
      this.isRecovering = false;
      this.metrics.successfulRestarts++;
      
      this._log('STT_RECOVERY_SUCCESS', {
        attempt: this.restartAttempts,
        totalSuccessful: this.metrics.successfulRestarts,
      });
      
      return true;
      
    } catch (error) {
      this.isRecovering = false;
      this.metrics.failedRestarts++;
      
      this._log('STT_RECOVERY_FAILED', {
        attempt: this.restartAttempts,
        error: error.message,
      });
      
      // Tentar novamente recursivamente
      return this.attemptRestart(recognition, reason);
    }
  }
  
  /**
   * Reseta o contador de tentativas (chamado após sucesso prolongado).
   */
  reset() {
    this.restartAttempts = 0;
    this.isRecovering = false;
    this._log('STT_RECOVERY_RESET');
  }
  
  /**
   * Verifica se deve tentar recovery.
   */
  shouldAttemptRecovery() {
    return this.restartAttempts < this.config.sttMaxRestartAttempts && !this.isRecovering;
  }
  
  /**
   * Retorna métricas de recovery.
   */
  getMetrics() {
    return { ...this.metrics };
  }
  
  /**
   * Log formatado.
   */
  _log(event, data = {}) {
    this.onLog(`[STTRecovery] ${event}`, data);
  }
}

// ============================================================================
// MOBILE RECOVERY ENGINE
// ============================================================================

/**
 * Engine de recovery geral para componentes de áudio.
 * Coordena recovery de AudioContext, MediaStream e STT.
 */
class MobileRecoveryEngine {
  constructor(options = {}) {
    this.platformConfig = getPlatformConfig();
    this.config = this.platformConfig.getConfig();
    
    // Managers
    this.audioContextManager = options.audioContextManager;
    this.mediaStreamManager = options.mediaStreamManager;
    this.sttRecoveryManager = options.sttRecoveryManager;
    
    // Callbacks
    this.onLog = options.onLog || console.log;
    this.onRecoveryStart = options.onRecoveryStart || (() => {});
    this.onRecoveryComplete = options.onRecoveryComplete || (() => {});
    this.onRecoveryFailed = options.onRecoveryFailed || (() => {});
    
    // Estado
    this.isRecovering = false;
    this.recoveryAttempts = 0;
    
    // Métricas
    this.metrics = {
      totalRecoveries: 0,
      audioContextRecoveries: 0,
      mediaStreamRecoveries: 0,
      successfulRecoveries: 0,
      failedRecoveries: 0,
    };
  }
  
  /**
   * Tenta recuperar o componente especificado.
   * @param {string} component - 'audioContext', 'mediaStream', 'stt', ou 'all'
   * @param {Object} context - Contexto adicional (recognition, etc)
   * @returns {Promise<boolean>}
   */
  async recover(component, context = {}) {
    if (this.isRecovering) {
      this._log('RECOVERY_ALREADY_IN_PROGRESS');
      return false;
    }
    
    if (this.recoveryAttempts >= this.config.maxRecoveryAttempts) {
      this._log('RECOVERY_MAX_ATTEMPTS_REACHED', {
        attempts: this.recoveryAttempts,
        max: this.config.maxRecoveryAttempts,
      });
      this.onRecoveryFailed('Maximum recovery attempts reached');
      return false;
    }
    
    this.isRecovering = true;
    this.recoveryAttempts++;
    this.metrics.totalRecoveries++;
    
    this._log('RECOVERY_STARTING', { component, attempt: this.recoveryAttempts });
    this.onRecoveryStart(component);
    
    try {
      let success = false;
      
      switch (component) {
        case 'audioContext':
          success = await this._recoverAudioContext();
          if (success) this.metrics.audioContextRecoveries++;
          break;
          
        case 'mediaStream':
          success = await this._recoverMediaStream();
          if (success) this.metrics.mediaStreamRecoveries++;
          break;
          
        case 'stt':
          success = await this.sttRecoveryManager?.attemptRestart(
            context.recognition, 
            context.reason
          );
          break;
          
        case 'all':
          success = await this._recoverAll(context);
          break;
          
        default:
          this._log('RECOVERY_UNKNOWN_COMPONENT', { component });
          success = false;
      }
      
      this.isRecovering = false;
      
      if (success) {
        this.metrics.successfulRecoveries++;
        this._log('RECOVERY_SUCCESS', { component });
        this.onRecoveryComplete(component);
      } else {
        this.metrics.failedRecoveries++;
        this._log('RECOVERY_FAILED', { component });
        this.onRecoveryFailed(`Failed to recover ${component}`);
      }
      
      return success;
      
    } catch (error) {
      this.isRecovering = false;
      this.metrics.failedRecoveries++;
      this._log('RECOVERY_ERROR', { component, error: error.message });
      this.onRecoveryFailed(error.message);
      return false;
    }
  }
  
  /**
   * Recupera AudioContext.
   */
  async _recoverAudioContext() {
    if (!this.audioContextManager) return false;
    
    await this.audioContextManager.close();
    await new Promise(r => setTimeout(r, this.config.recoveryDelay));
    
    await this.audioContextManager.create();
    return this.audioContextManager.isReady() || 
           await this.audioContextManager.unlock();
  }
  
  /**
   * Recupera MediaStream.
   */
  async _recoverMediaStream() {
    if (!this.mediaStreamManager) return false;
    
    this.mediaStreamManager.release();
    await new Promise(r => setTimeout(r, this.config.recoveryDelay));
    
    await this.mediaStreamManager.acquire();
    return !!this.mediaStreamManager.getStream();
  }
  
  /**
   * Recupera todos os componentes.
   */
  async _recoverAll(context) {
    const audioSuccess = await this._recoverAudioContext();
    if (!audioSuccess) return false;
    
    const mediaSuccess = await this._recoverMediaStream();
    if (!mediaSuccess) return false;
    
    return true;
  }
  
  /**
   * Reseta contadores de recovery.
   */
  reset() {
    this.recoveryAttempts = 0;
    this.isRecovering = false;
    this.sttRecoveryManager?.reset();
    this._log('RECOVERY_ENGINE_RESET');
  }
  
  /**
   * Retorna métricas.
   */
  getMetrics() {
    return {
      ...this.metrics,
      stt: this.sttRecoveryManager?.getMetrics(),
    };
  }
  
  /**
   * Log formatado.
   */
  _log(event, data = {}) {
    this.onLog(`[MobileRecovery] ${event}`, data);
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export {
  AudioContextManager,
  MediaStreamManager,
  STTRecoveryManager,
  MobileRecoveryEngine,
};

export default {
  AudioContextManager,
  MediaStreamManager,
  STTRecoveryManager,
  MobileRecoveryEngine,
};
